import { useMemo, useState } from 'react'
import he from 'he'
import useFavurTranslation from 'hooks/useFavurTranslation'
import { useJamesApolloQuery } from 'hooks/useJamesApolloQuery'
import usePersonsWithTeamPermissions from 'hooks/usePersonsWithTeamPermissions'
import { balanceOfficeOverviewQuery, improvedBalancesQuery } from 'pages/Tasks/AbsenceRequest/queries'
import { ZERO } from 'shared/constants'
import type { PersonT } from 'types'
import type { BalanceObject } from 'types/balances'
import { newDateWithoutTime } from 'utils/date'
import { getUserName } from 'utils/person'
import { MULTIPLE_TENANTS_COUNT } from './constants'
import type { BalanceModalProps } from './types'

export const useBalanceModal = ({ task, date, open, selectedPersonUuid }: BalanceModalProps) => {
  const { t } = useFavurTranslation()
  const { favurUuid, person } = task ?? {}
  const [frontlineBalances, setFrontlineBalances] = useState<BalanceObject[]>()
  const [officeBalances, setOfficeBalances] = useState<BalanceObject[]>()
  const estimateFromDay = useMemo(() => (date ? date.toISOString() : newDateWithoutTime().toISOString()), [date])
  const { personTeamPermissions, loading: loadingPTP } = usePersonsWithTeamPermissions()
  const personIds = useMemo(() => {
    const selectedPerson = personTeamPermissions?.find((userPerson) => userPerson.favurUuid === selectedPersonUuid)

    if (!selectedPerson) return []

    return [selectedPerson.id]
  }, [personTeamPermissions, selectedPersonUuid])

  const { loading: officeBalancesLoading } = useJamesApolloQuery<{
    balanceOfficeOverview: BalanceObject[]
  }>(balanceOfficeOverviewQuery, {
    variables: {
      estimateFromDay,
      taskFavurUuid: favurUuid,
    },
    onCompleted: (result) => {
      setOfficeBalances(result?.balanceOfficeOverview)
    },
    skip: !open || !task,
  })

  const { loading: balancesLoading } = useJamesApolloQuery<{ improvedBalances?: BalanceObject[] }>(
    improvedBalancesQuery,
    {
      variables: {
        estimateFromDay,
        personIds: personIds,
      },
      onCompleted: (result) => {
        setFrontlineBalances(result?.improvedBalances)
      },
      skip: !selectedPersonUuid || loadingPTP || !open,
    },
  )

  const balances = task ? officeBalances : frontlineBalances
  const loading = task ? officeBalancesLoading : balancesLoading

  const frontlinerFullName = person && getUserName(person as PersonT, true)
  const emptyTable = useMemo(() => !loading && balances?.length === ZERO, [balances?.length, loading])

  const selectedTenantName = useMemo(() => {
    if (!personTeamPermissions || personTeamPermissions.length < MULTIPLE_TENANTS_COUNT) return undefined

    const selectedPerson = personTeamPermissions.find((ptp) => ptp.favurUuid === selectedPersonUuid)

    return selectedPerson?.tenant.name
  }, [personTeamPermissions, selectedPersonUuid])

  const titleLabel = useMemo(() => {
    if (emptyTable) return 'absenceRequest.components.employeeSaldo.noData'

    const titleKey = frontlinerFullName
      ? 'absenceRequest.components.employeeSaldo.titleOffice'
      : 'absenceRequest.components.employeeSaldo.title'

    const labelWithName = he.decode(t(titleKey, { frontlinerFullName }))

    return selectedTenantName ? `${labelWithName} | ${selectedTenantName}` : labelWithName
  }, [emptyTable, frontlinerFullName, selectedTenantName, t])

  return { balances, loading, open, titleLabel, emptyTable }
}
